import React, { useState } from "react";
import API_URL from "./config";
import { getTeamTheme } from "./teamTheme";
import "./TradeModal.css";

// ── Player value formula (mirrors TradeModal.js) ──────────
const avg = (arr) => arr.reduce((a, b) => a + b, 0) / arr.length;
const playerValue = (p) => {
  const offense = avg([p.close || 50, p.mid || 50, p.three || 50, p.free_throw || 50, p.dunk || 50, p.passing || 50, p.dribbling || 50, p.offensive_iq || 50]);
  const defense = avg([p.defensive_iq || 50, p.rebounding || 50, p.strength || 50, p.jumping || 50]);
  const athletic = avg([p.speed || 50, p.stamina || 50, p.jumping || 50]);
  return Math.round(offense * 0.45 + defense * 0.35 + athletic * 0.2);
};

// Calculate fairness from YOUR perspective
// Positive diff = good for you (they give more value than they want)
// Negative diff = bad for you (they want more value than they give)
const fairnessLabel = (diff) => {
  if (diff >= 5)  return { text: "Great Trade",    color: "#10b981" };
  if (diff >= 3)  return { text: "Good Trade",      color: "#34d399" };
  if (diff >= -2)  return { text: "Even Trade",       color: "#94a3b8" };
  if (diff >= -5) return { text: "Bad Trade",       color: "#f97316" };
  return               { text: "Terrible Trade",     color: "#ef4444" };
};

const PlayerCard = ({ player, teamName, theme, isRequested = false }) => {
  if (!player) return null;
  
  const value = playerValue(player);
  
  return (
    <div 
      className="opponent-trade-player-card"
      style={{
        borderLeft: `4px solid ${theme?.colors?.primary || theme?.primary || "#1252d2"}`
      }}
    >
      <div className="opponent-trade-player-header">
        <div>
          <div className="opponent-trade-player-name">{player.name}</div>
          <div className="opponent-trade-player-pos">{player.position}</div>
        </div>
        <div className="opponent-trade-player-value">{value}</div>
      </div>
      <div className="opponent-trade-player-footer">
        {isRequested && <span className="opponent-trade-badge">Requested</span>}
        {!isRequested && <span className="opponent-trade-badge opponent-offered">Offered</span>}
      </div>
    </div>
  );
};

export default function OpponentTradeModal({ 
  tradeProposal, 
  userTeamId, 
  onClose, 
  onTradeComplete 
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null); // "accepted" | "rejected" | null

  if (!tradeProposal) return null;

  const isUserWinner = tradeProposal.receivingTeamId === userTeamId;
  if (!isUserWinner) return null; // Only show if user is the one receiving proposal

  const proposingTeamName = tradeProposal.proposingTeamName;
  const proposingTeamTheme = getTeamTheme(proposingTeamName);
  const userTeamTheme = getTeamTheme(tradeProposal.receivingTeamName);

  const theirPlayerValue = playerValue(tradeProposal.offeringPlayer);
  const yourPlayerValue = playerValue(tradeProposal.proposedPlayerFromWinner);
  // Positive diff = good for you (they're giving more value than asking)
  const diff = theirPlayerValue - yourPlayerValue;
  const fairness = fairnessLabel(diff);

  const handleAccept = async () => {
    setLoading(true);
    setError("");
    try {
      const activeSaveId = localStorage.getItem("activeSaveId");
      const user = JSON.parse(localStorage.getItem("user") || "{}");

      const res = await fetch(`${API_URL}/api/saves/${activeSaveId}/trade`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-User-ID": user._id,
        },
        body: JSON.stringify({
          myPlayerId: tradeProposal.proposedPlayerFromWinner._id,
          theirPlayerId: tradeProposal.offeringPlayer._id,
          myTeamId: userTeamId,
          theirTeamId: tradeProposal.proposingTeamId,
          isOpponentInitiated: true,
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || "Trade failed.");
        setLoading(false);
        return;
      }
      
      setResult("accepted");
      setTimeout(() => {
        if (onTradeComplete) onTradeComplete();
        onClose();
      }, 1500);
    } catch (e) {
      setError("Server error during trade.");
      setLoading(false);
    }
  };

  const handleDecline = () => {
    setResult("rejected");
    setTimeout(() => {
      if (onTradeComplete) onTradeComplete();
      onClose();
    }, 1500);
  };

  return (
    <div className="trade-overlay" onClick={onClose}>
      <div className="opponent-trade-modal" onClick={e => e.stopPropagation()}>
        
        {/* Header */}
        <div className="opponent-trade-header">
          <h2>Trade Proposal</h2>
          <p className="opponent-trade-subtitle">
            {proposingTeamName} wants to make a deal
          </p>
        </div>

        {/* Content */}
        <div className="opponent-trade-content">
          {error && <div className="opponent-trade-error">{error}</div>}

          {result === null ? (
            <>
              {/* Trade visualization */}
              <div className="opponent-trade-comparison">
                <div className="opponent-trade-side">
                  <div className="opponent-trade-side-label">They're Offering</div>
                  <PlayerCard 
                    player={tradeProposal.offeringPlayer}
                    teamName={proposingTeamName}
                    theme={proposingTeamTheme}
                    isRequested={false}
                  />
                </div>

                <div className="opponent-trade-arrow">
                  <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                    <path d="M10 20 L30 20" stroke="currentColor" strokeWidth="2"/>
                    <path d="M25 15 L30 20 L25 25" stroke="currentColor" strokeWidth="2" fill="none"/>
                  </svg>
                </div>

                <div className="opponent-trade-side">
                  <div className="opponent-trade-side-label">They Want</div>
                  <PlayerCard 
                    player={tradeProposal.proposedPlayerFromWinner}
                    teamName={tradeProposal.receivingTeamName}
                    theme={userTeamTheme}
                    isRequested={true}
                  />
                </div>
              </div>

              {/* Fairness indicator */}
              <div className="opponent-trade-fairness">
                <div className="opponent-trade-fairness-label">Trade Balance:</div>
                <div 
                  className="opponent-trade-fairness-badge"
                  style={{ color: fairness.color }}
                >
                  {fairness.text}
                </div>
                <div className="opponent-trade-value-diff">
                  {diff > 0 ? (
                    <>You gain <strong>{Math.abs(diff)}</strong> OVR point{Math.abs(diff) !== 1 ? "s" : ""}</>
                  ) : diff < 0 ? (
                    <>You lose <strong>{Math.abs(diff)}</strong> OVR point{Math.abs(diff) !== 1 ? "s" : ""}</>
                  ) : (
                    <>Perfectly balanced</>
                  )}
                </div>
              </div>

              {/* Buttons */}
              <div className="opponent-trade-actions">
                <button
                  className="opponent-trade-accept"
                  onClick={handleAccept}
                  disabled={loading}
                >
                  {loading ? "Processing..." : "✓ Accept"}
                </button>
                <button
                  className="opponent-trade-decline"
                  onClick={handleDecline}
                  disabled={loading}
                >
                  ✕ Decline
                </button>
              </div>
            </>
          ) : (
            /* Result display */
            <div className={`opponent-trade-result ${result}`}>
              {result === "accepted" ? (
                <>
                  <div className="opponent-trade-result-icon">✓</div>
                  <div className="opponent-trade-result-text">Trade accepted!</div>
                  <div className="opponent-trade-result-detail">Swap completed successfully.</div>
                </>
              ) : (
                <>
                  <div className="opponent-trade-result-icon">✕</div>
                  <div className="opponent-trade-result-text">Trade declined</div>
                  <div className="opponent-trade-result-detail">{proposingTeamName} will look elsewhere.</div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
